import React, { useState, useEffect } from 'react';
import { Save, Eye, EyeOff, Megaphone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRESET_COLORS = [
  { bg: '#D97706', text: '#FFFFFF', name: 'כתום' },
  { bg: '#DC2626', text: '#FFFFFF', name: 'אדום' },
  { bg: '#059669', text: '#FFFFFF', name: 'ירוק' },
  { bg: '#2563EB', text: '#FFFFFF', name: 'כחול' },
  { bg: '#7C3AED', text: '#FFFFFF', name: 'סגול' },
  { bg: '#1a1a2e', text: '#FFFFFF', name: 'כהה' },
  { bg: '#FBBF24', text: '#000000', name: 'צהוב' },
  { bg: '#000000', text: '#FBBF24', name: 'שחור+זהב' },
];

const AdminBanner = () => {
  const [form, setForm] = useState({ text: '', link: '', bg_color: '#D97706', text_color: '#FFFFFF', active: true, free_shipping_threshold: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axios.get(`${API}/banner`);
        if (r.data.text) setForm({ text: r.data.text, link: r.data.link || '', bg_color: r.data.bg_color || '#D97706', text_color: r.data.text_color || '#FFFFFF', active: r.data.active !== false, free_shipping_threshold: r.data.free_shipping_threshold || 0 });
      } catch (e) {}
      setLoading(false);
    };
    fetch();
  }, []);

  const save = async () => {
    try {
      await axios.post(`${API}/admin/banner`, form);
      toast.success('באנר עודכן');
    } catch (e) { toast.error('שגיאה בשמירה'); }
  };

  if (loading) return null;

  return (
    <div dir="rtl" data-testid="admin-banner-section">
      <h2 className="text-xl font-black text-[#2C3E2D] mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
        <Megaphone className="w-5 h-5 inline ml-2" />ניהול באנר
      </h2>

      {/* Preview */}
      <div className="mb-4">
        <p className="text-xs text-stone-400 font-bold mb-2">תצוגה מקדימה:</p>
        <div
          className="w-full py-2.5 px-4 text-center rounded-xl"
          style={{ backgroundColor: form.bg_color, color: form.text_color }}
          data-testid="banner-preview"
        >
          <span className="text-sm font-bold">{form.text || 'טקסט הבאנר יופיע כאן...'}</span>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl p-5 border border-stone-200 space-y-4">
        {/* Active Toggle */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-stone-700">סטטוס באנר</span>
          <button
            onClick={() => setForm({ ...form, active: !form.active })}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${form.active ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-400'}`}
            data-testid="banner-toggle"
          >
            {form.active ? <><Eye className="w-4 h-4" /> פעיל - מוצג באתר</> : <><EyeOff className="w-4 h-4" /> מוסתר</>}
          </button>
        </div>

        {/* Text */}
        <div>
          <label className="text-xs text-stone-400 block mb-1">טקסט פרסומי</label>
          <input
            value={form.text}
            onChange={e => setForm({ ...form, text: e.target.value })}
            placeholder="למשל: משלוח חינם בהזמנה מעל ₪300!"
            className="border rounded-lg px-3 py-2.5 text-sm w-full"
            data-testid="banner-text-input"
          />
        </div>

        {/* Free Shipping Threshold */}
        <div>
          <label className="text-xs text-stone-400 block mb-1">משלוח חינם מסכום (₪) - 0 = כבוי</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={form.free_shipping_threshold}
              onChange={e => {
                const val = parseFloat(e.target.value) || 0;
                setForm({ ...form, free_shipping_threshold: val, text: val > 0 ? `משלוח חינם בהזמנה מעל ₪${val}! | הזמינו עכשיו` : form.text });
              }}
              placeholder="450"
              className="border rounded-lg px-3 py-2.5 text-sm w-32"
              data-testid="banner-free-shipping-input"
            />
            <span className="text-xs text-stone-400">₪</span>
            {form.free_shipping_threshold > 0 && (
              <span className="text-xs text-green-600 font-bold bg-green-50 px-2 py-1 rounded">פעיל - משלוח חינם מעל ₪{form.free_shipping_threshold}</span>
            )}
          </div>
        </div>

        {/* Link */}
        <div>
          <label className="text-xs text-stone-400 block mb-1">קישור (אופציונלי - לחיצה תוביל לדף)</label>
          <input
            value={form.link}
            onChange={e => setForm({ ...form, link: e.target.value })}
            placeholder="/products או https://..."
            className="border rounded-lg px-3 py-2.5 text-sm w-full"
            dir="ltr"
            data-testid="banner-link-input"
          />
        </div>

        {/* Color Presets */}
        <div>
          <label className="text-xs text-stone-400 block mb-2">עיצוב צבע</label>
          <div className="flex flex-wrap gap-2">
            {PRESET_COLORS.map((c, i) => (
              <button
                key={i}
                onClick={() => setForm({ ...form, bg_color: c.bg, text_color: c.text })}
                className={`px-3 py-2 rounded-lg text-xs font-bold border-2 transition-all ${form.bg_color === c.bg ? 'border-[#D97706] scale-105' : 'border-transparent'}`}
                style={{ backgroundColor: c.bg, color: c.text }}
                data-testid={`color-preset-${i}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Colors */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-stone-400 block mb-1">צבע רקע</label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.bg_color} onChange={e => setForm({ ...form, bg_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
              <input value={form.bg_color} onChange={e => setForm({ ...form, bg_color: e.target.value })} className="border rounded-lg px-2 py-1.5 text-xs flex-1 font-mono" dir="ltr" />
            </div>
          </div>
          <div>
            <label className="text-xs text-stone-400 block mb-1">צבע טקסט</label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.text_color} onChange={e => setForm({ ...form, text_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
              <input value={form.text_color} onChange={e => setForm({ ...form, text_color: e.target.value })} className="border rounded-lg px-2 py-1.5 text-xs flex-1 font-mono" dir="ltr" />
            </div>
          </div>
        </div>

        {/* Save */}
        <button
          onClick={save}
          className="w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-xl py-3 font-bold text-sm flex items-center justify-center gap-2 transition-colors"
          data-testid="banner-save-btn"
        >
          <Save className="w-4 h-4" /> שמור באנר
        </button>
      </div>
    </div>
  );
};

export default AdminBanner;
